import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiClient';

// Clock vs Schedule (Pat, Sep 25 2026): the day's timesheets held against the day's
// schedule, both from QuickBooks Time. Early clock-ins, the wrong job, and field people
// on the clock with no shift scheduled.
const KIND = {
  early:       { label: 'Clocked in early',      tint: '#b45309', bg: '#fffbeb', icon: '⏰' },
  wrong:       { label: 'On a job not scheduled', tint: '#b91c1c', bg: '#fef2f2', icon: '🔀' },
  unscheduled: { label: 'On the clock, not on the schedule', tint: '#6b21a8', bg: '#faf5ff', icon: '📅' },
};
const btn = { padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' };
const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const clock = iso => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—');

export default function ClockCheckPanel() {
  const navigate = useNavigate();
  const [date, setDate] = useState(() => ymd(new Date()));
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = d => apiFetch(`/clock-check?date=${d}`).then(r => { setData(r); setErr(''); }).catch(e => setErr(e?.error || 'Could not read QuickBooks Time.'));
  useEffect(() => { setData(null); load(date); const t = setInterval(() => load(date), 120000); return () => clearInterval(t); }, [date]);
  const shift = n => { const d = new Date(date + 'T12:00:00'); d.setDate(d.getDate() + n); setDate(ymd(d)); };
  const flags = data?.flags || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <button onClick={() => shift(-1)} style={btn}>‹</button>
        <input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} style={{ ...btn, fontFamily: 'inherit' }} />
        <button onClick={() => shift(1)} style={btn}>›</button>
        <button onClick={() => setDate(ymd(new Date()))} style={btn}>Today</button>
        {data && <span style={{ fontSize: 12.5, color: '#6b7280', marginLeft: 8 }}>{data.people} on timesheets · {data.scheduled} on the schedule · early means more than {data.grace_min} minutes before the shift</span>}
        <span style={{ flex: 1 }} />
        {data && ['early', 'wrong', 'unscheduled'].map(k => (
          <span key={k} style={{ fontSize: 12, fontWeight: 800, padding: '3px 9px', borderRadius: 10, background: KIND[k].bg, color: KIND[k].tint }}>{KIND[k].icon} {data.counts[k]}</span>
        ))}
      </div>
      {err && <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 700, marginBottom: 10 }}>{err}</div>}
      {!data && !err && <div style={{ color: '#6b7280', padding: 20 }}>Reading QuickBooks Time…</div>}
      {data && flags.length === 0 && (
        <div style={{ color: '#15803d', padding: 24, textAlign: 'center', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, fontWeight: 700 }}>
          ✓ Nothing to flag {date === ymd(new Date()) ? 'so far today' : 'that day'} — everyone clocked in on time and on the job they were scheduled for.
        </div>
      )}
      {data && flags.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {flags.map((f, i) => {
            const k = KIND[f.kind];
            return (
              <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderLeft: `5px solid ${k.tint}`, borderRadius: 10, padding: '10px 14px', display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11.5, fontWeight: 800, padding: '2px 8px', borderRadius: 10, background: k.bg, color: k.tint, whiteSpace: 'nowrap' }}>{k.icon} {k.label}</span>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{f.user}{f.on_clock && <span style={{ marginLeft: 8, fontSize: 11, color: '#15803d', fontWeight: 700 }}>● on the clock</span>}</div>
                  <div style={{ fontSize: 13, color: '#374151', marginTop: 3 }}>
                    {f.kind === 'early' && <>Clocked in <b>{clock(f.clock_in)}</b>, scheduled <b>{clock(f.sched_start)}</b> — <b>{f.minutes} min early</b>{f.scheduled_jobs?.length ? ` · on ${f.scheduled_jobs.join(', ')}` : ''}</>}
                    {f.kind === 'wrong' && <>Clocked into <b>{f.clocked_job}</b> at {clock(f.clock_in)} — scheduled on <b>{f.scheduled_jobs.join(', ')}</b></>}
                    {f.kind === 'unscheduled' && <>Clocked into <b>{f.clocked_job}</b> at {clock(f.clock_in)} — nothing on the schedule for them that day</>}
                  </div>
                </div>
                {f.project_id && <button onClick={() => navigate(`/admin/new-projects/${f.project_id}`)} style={btn}>Open the job ›</button>}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 12, lineHeight: 1.5 }}>Both sides come from QuickBooks Time: the published schedule and the timesheets. A person with no shift that day is only flagged if they are field or shop; the office and PMs clock in without being scheduled. Refreshes every two minutes.</div>
    </div>
  );
}
